import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Badge, Table } from '@syneto/compass-react';
import _ from 'lodash';
import { StoreContext } from '../../contexts/StoreContext';
import { ServiceContext } from '../../contexts/ServiceContext';
import { NotificationContext } from '../../contexts/NotificationContext';
import { getOSVersion, parseOSVersion, isOSVersionBelow } from '../../helpers/OSVersionParser';
import { MachineDetails } from './components/MachineDetails';
import { UpgradeChannelBadge } from './components/UpgradeChannelBadge';
import { ExpirationDateCell } from './components/ExpirationDateCell';
import { AddonsAlertCell } from './components/AddonsAlertCell';
import { compareAddonsAlerts, compareExpirationDates } from './InventorySort';
import { buildTableMachine } from './buildTableMachine';
import { buildAddonsPanel } from './buildAddonsPanel';
import { useExpiringInterval } from './useExpiringInterval';
import './style.scss';
import { Loading } from '../../components/Loading';
import { LoadFailedMessage } from '../../components/LoadFailedMessage';
import { Does } from '../../components/Access/Does';
import pdfMake from 'pdfmake/build/pdfmake';
import vfsFonts from 'pdfmake/build/vfs_fonts';
import maintenanceCertificationTemplate from './pdf/maintenanceCertificationTemplate';
import InfoIcon from '../../components/Form/InfoIcon';
import { LegacyOSVersionBanner } from './components/LegacyOSVersionBanner';
import { PartnerPreviewProgramBanner } from './components/PartnerPreviewProgramBanner';
import { BANNER_KEYS } from '../../services/BannerDismissal';

// Version-reporting accuracy is not a Partner Preview concept, so this threshold and the
// warning it drives are independent of company participation.
const LEGACY_OS_VERSION_THRESHOLD = '5.4.4';

export const Inventory = () => {
	const location = useLocation();

	const { machineService, locationService, companyUserService, accessService, partnerPreviewService, licensingService, bannerDismissalService } = useContext(ServiceContext);
	const { state } = useContext(StoreContext);
	const { loggedInUser, osLatestVersions } = state;
	const { pushDangerNotification, pushSuccessfulNotification } = useContext(NotificationContext);

	const [machines, setMachines] = useState([]);
	const [selectedMachine, setSelectedMachine] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [partnerPreviewEnabled, setPartnerPreviewEnabled] = useState(false);
	// The banner may not choose between "enrolled" and "invite" before the participation read has
	// settled, or an enrolled company would briefly see the invitation.
	const [partnerPreviewParticipationLoaded, setPartnerPreviewParticipationLoaded] = useState(false);
	const [enrollmentsByMachineId, setEnrollmentsByMachineId] = useState({});
	// Increments after every completed enrollment refresh, whether or not the map's values
	// actually changed, so the side pane can re-derive its displayed toggle from the refreshed
	// prop even when the refresh reports the same value the optimistic update already showed.
	const [enrollmentsRefreshCount, setEnrollmentsRefreshCount] = useState(0);
	// Per machine id: the reported upgrade channel and the add-ons, both from the one batch call.
	const [productsByMachineId, setProductsByMachineId] = useState({});
	// True after a failed batch call; the pane then says the add-ons could not be loaded instead of staying silent.
	const [productsLoadFailed, setProductsLoadFailed] = useState(false);
	// null until the company setting resolves; see useExpiringInterval.
	const expiringInterval = useExpiringInterval();
	// Asks the side pane to scroll to its Add-ons section. One-shot: the section handles it and the page clears it
	// (handleAddonsSectionRequestHandled), so it can never fire later. Clicking the icon of the machine that is
	// already selected sets a fresh object, which scrolls again.
	const [addonsSectionRequest, setAddonsSectionRequest] = useState(null);
	const [bannerDismissals, setBannerDismissals] = useState([]);
	// Neither the banner nor the header icon may render while this is true - otherwise the page
	// would flash the banner before it turns into the icon on the same load.
	const [bannerDismissalsLoading, setBannerDismissalsLoading] = useState(true);

	const defaultFilterLocation = location?.state?.locationName || '';

	// The machine's id is its product id, which is what carries the channel the appliance last
	// reported. A machine that has never synced comes back with a null channel from the service
	// mapper and is not what reaches this catch -- reaching it means the batch lookup itself
	// failed (the endpoint fails closed for the whole batch if any single product id cannot be
	// resolved). On failure, the previously loaded map is left in place rather than wiped to {}
	// -- losing every machine's Version Type because one refresh failed is never the better
	// outcome -- and the failure is surfaced via a notification instead of being swallowed.
	// The same call carries the add-ons, so a failure also leaves them as they were (unknown on first load).
	const loadProductsBatch = useCallback(async (machinesToLoad) => {
		if (!machinesToLoad.length) {
			setProductsByMachineId({});
			return;
		}

		try {
			const byMachineId = await licensingService.getProductsBatch(
				machinesToLoad.map((machine) => { return machine.id; })
			);
			setProductsByMachineId(byMachineId);
			setProductsLoadFailed(false);
		} catch (error) {
			setProductsLoadFailed(true);
			pushDangerNotification('Failed to fetch machine version types and add-ons.');
		}
	}, []);

	const loadMachinesData = useCallback(async () => {
		let loadedMachines = null;

		try {
			const locations = await locationService.getLocations();
			const locationIds = locations.map((location) => { return location.id; });
			const machines = accessService.hasPermissionOnCompanyResource('location.create')
				? await machineService.getMachinesByCompanyId(loggedInUser.company.id)
				: locationIds.length
					? await machineService.getMachinesByLocationIds(locationIds)
					: [];
			// Without user.fetch on the company the users stay empty and each machine's "Activated by"
			// reads N/A; the machines themselves load as usual.
			const { users } = await companyUserService.getCompanyUsers(loggedInUser.company.id);

			machineService.attachDependenciesToMachines(locations, users, machines);

			const locationMachines = machines.filter((m) => { return m.location === defaultFilterLocation; });

			if (locationMachines.length) { setSelectedMachine(locationMachines[0]); }

			setLoadFailed(false);
			setMachines(machines);
			loadedMachines = machines;
		} catch (error) {
			setLoadFailed(true);
			pushDangerNotification('Failed to fetch inventory data.');
		} finally {
			setLoading(false);
		}

		// A failed load leaves the version types and add-ons it already had: with no machines the lookup would reset them.
		if (loadedMachines) {
			await loadProductsBatch(loadedMachines);
		}
	}, [selectedMachine]);

	const retryLoadingMachines = () => {
		setLoading(true);
		loadMachinesData();
	};

	// The machines list is unpaginated, so every machine's badge/action depends on having its
	// enrollment loaded. Page through all enrollments rather than relying on the endpoint's
	// default page size, which would leave later machines unbadged.
	// On failure, the previous map is left in place rather than wiped to {} -- losing every
	// machine's state because one refresh failed is never the better outcome -- and the error
	// is rethrown so a refresh triggered by a toggle does not report success it did not earn.
	const loadPartnerPreviewEnrollments = useCallback(async () => {
		const byMachineId = {};
		const limit = 200;
		let start = 0;
		let total = 0;

		do {
			const { enrollments, pagination } = await partnerPreviewService.getEnrollments(
				loggedInUser.company.id,
				start,
				limit
			);

			enrollments.forEach((enrollment) => {
				byMachineId[enrollment.productId] = { enrollmentId: enrollment.id, enabled: enrollment.enabled };
			});

			total = pagination?.totalItems ?? enrollments.length;
			start += limit;
		} while (start < total);

		setEnrollmentsByMachineId(byMachineId);
		setEnrollmentsRefreshCount((previousCount) => { return previousCount + 1; });
	}, []);

	const loadPartnerPreviewParticipation = useCallback(async () => {
		try {
			const participation = await partnerPreviewService.getParticipation(loggedInUser.company.id);
			setPartnerPreviewEnabled(Boolean(participation?.enabled));
		} catch (error) {
			setPartnerPreviewEnabled(false);
		}

		setPartnerPreviewParticipationLoaded(true);

		try {
			await loadPartnerPreviewEnrollments();
		} catch (error) {
			// Nothing to report a failure to at mount time; a toggle-triggered refresh is what
			// surfaces this failure to the user, via togglePartnerPreviewForMachine's own catch.
		}
	}, [loadPartnerPreviewEnrollments]);

	// Fetched once here rather than inside the banner: the same dismissal data also decides
	// whether the OS column header shows its exclamation icon. A failed read is not dangerous -
	// it just means the user has not dismissed anything, so the banner (correctly) shows again.
	const loadBannerDismissals = useCallback(async () => {
		try {
			const dismissals = await bannerDismissalService.getDismissals(loggedInUser.company.id);
			setBannerDismissals(dismissals);
		} catch (error) {
			setBannerDismissals([]);
		} finally {
			setBannerDismissalsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadMachinesData();

		return () => {
			setLoading(true);
		};
	}, []);

	useEffect(() => {
		loadPartnerPreviewParticipation();
	}, []);

	useEffect(() => {
		loadBannerDismissals();
	}, []);

	// react-bootstrap-table-next only re-renders a formatter cell when the row object itself
	// changes, so the per-machine enrollment, the reported upgrade channel and the expiry values
	// must live on the row (not in external state); buildTableMachine folds them in.
	const tableMachines = useMemo(() => {
		return machines.map((machine) => {
			return buildTableMachine(machine, {
				enrollment: enrollmentsByMachineId[machine.id],
				product: productsByMachineId[machine.id],
				interval: expiringInterval,
			});
		});
	}, [machines, enrollmentsByMachineId, productsByMachineId, expiringInterval]);

	// The Add-ons icon only opens the pane for users who can see it (the pane is gated by appliance.view).
	const canViewMachineDetails = accessService.hasPermissionOnAnyResource('appliance.view');

	// The row's own click already selects the machine; selecting here as well keeps keyboard activation working.
	const handleAddonsAlertClick = (row) => {
		setSelectedMachine(row);
		setAddonsSectionRequest({ machineId: row.id });
	};

	// Stable on purpose: AddonsSection lists it as an effect dependency.
	const handleAddonsSectionRequestHandled = useCallback(() => {
		setAddonsSectionRequest(null);
	}, []);

	const hasLegacyOSVersionMachine = useMemo(() => {
		return machines.some((machine) => { return isOSVersionBelow(machine.osVersion, LEGACY_OS_VERSION_THRESHOLD); });
	}, [machines]);

	// Banner and header icon are mutually exclusive by design: before dismissal only the banner
	// shows, after dismissal only the header icon shows.
	const legacyOSVersionBannerDismissed = useMemo(() => {
		return bannerDismissals.some((dismissal) => { return dismissal.bannerKey === BANNER_KEYS.LEGACY_OS_VERSION; });
	}, [bannerDismissals]);

	const renderLocation = (cell, row) => {
		return <div>{row.location ? row.location : 'N/A'}</div>;
	};

	const locationFilter = () => {
		const locations = new Set();
		const opts = [];
		machines.forEach((machine) => { return locations.add(machine.location); });
		locations.forEach((location) => { return location && opts.push({ label: location, value: location }); });

		return _.orderBy(opts, ['label'], ['asc']);
	};

	const renderHostName = (cell, row) => {
		return <div>{row.name ? row.name : 'N/A'}</div>;
	};

	const renderModel = (cell, row) => {
		return <div><span className='me-2'>{row.productModel.title}</span>{row.maintenanceServices[0].maintenancePlan.customerFacingName.toLowerCase() === 'software only trial' ? <Badge appearance="secondary">Trial</Badge> : ''}</div>;
	};

	const renderPartnerPreviewInfo = (cell, row) => {
		return partnerPreviewEnabled && row.partnerPreviewEnrollment?.enabled ? (
			<InfoIcon
				placement="top"
				text={
					<>
						This machine is enrolled in the Partner Preview Program.
						An administrator can enable "Preview" updates from this machine's side pane.
					</>
				}
				iconColor="#22a0e9"
			/>
		) : null;
	};

	// Whether a machine is out of date is decided only by installed version vs the latest GA
	// release for its OS generation - never by the machine's Version Type. A Preview machine on
	// 6.0.3 is just as outdated as a Stable one, and a Preview machine ahead of GA is not.
	// An unknown latest version (no entry for this OS generation, or a failed lookup that fell
	// back to a catalogue without it) parses to 'N/A', which isOSVersionBelow reports as false,
	// so no warning is shown rather than a wrong one.
	const renderOSVersion = (cell, row) => {
		const machineCurrentVersion = parseOSVersion(row.osVersion);
		const osVersion = getOSVersion(row.osVersion);
		const osLatestVersion = osLatestVersions && osVersion ?  parseOSVersion(osLatestVersions[osVersion]) : 'N/A';

		return (
			<div className="d-flex align-items-center">
				<div className="me-3">{machineCurrentVersion ? machineCurrentVersion : 'N/A'}</div>
				{isOSVersionBelow(machineCurrentVersion, osLatestVersion) && (
					<InfoIcon placement="right" text="Your system is not up to date." iconType="exclamation" iconColor="#f4be00" />
				)}
			</div>
		);
	};

	const renderUpgradeChannel = (cell, row) => {
		return <UpgradeChannelBadge currentUpgradeChannel={row.currentUpgradeChannel} />;
	};

	const renderExpirationDate = (cell, row) => {
		return <ExpirationDateCell calendarDate={row.expirationDate} expiringInDays={row.maintenanceExpiringInDays} />;
	};

	const renderAddonsAlert = (cell, row) => {
		return (
			<AddonsAlertCell
				alert={row.addonsAlert}
				onClick={canViewMachineDetails ? () => { return handleAddonsAlertClick(row); } : undefined}
			/>
		);
	};

	// Shared by the banner's "Dismiss" button and its close icon: both persist the same
	// dismissal. On failure the banner is left visible - hiding it would suggest a dismissal that
	// was never persisted, and it would return on the next login anyway; the write is idempotent,
	// so the user can just click again.
	const handleDismissLegacyOSVersionBanner = useCallback(async () => {
		try {
			const dismissal = await bannerDismissalService.dismissBanner(loggedInUser.company.id, BANNER_KEYS.LEGACY_OS_VERSION);
			// The key is the one just sent, not the one echoed back: a 204, an empty body or a
			// snake_case field would silently leave the appended entry unmatched by the
			// legacyOSVersionBannerDismissed check, so the banner would never hide even though the
			// write succeeded. The response is still spread in so a well-formed one is used.
			setBannerDismissals((previousDismissals) => {
				return [...previousDismissals, { ...dismissal, bannerKey: BANNER_KEYS.LEGACY_OS_VERSION }];
			});
		} catch (error) {
			// Keep the banner up; see comment above.
		}
	}, []);

	const generateMaintenanceCertificationPdf = (expirationDate, maintenancePlan, serialNumber) => {
		const docDefinition = maintenanceCertificationTemplate(expirationDate, maintenancePlan, serialNumber);
		const { vfs } = vfsFonts.pdfMake;

		pdfMake.vfs = vfs;
		pdfMake.createPdf(docDefinition).download(`${new Date().valueOf()}.pdf`);
	};

	// Owns the mutation because it owns enrollmentsByMachineId/loadPartnerPreviewEnrollments; the
	// side pane only knows whether to keep its optimistic value or revert it, so this resolves on
	// success and rejects on failure rather than returning a status flag.
	const togglePartnerPreviewForMachine = useCallback(async (machine, enable) => {
		const enrollment = enrollmentsByMachineId[machine.id];

		try {
			const { jobId } = enrollment
				? await partnerPreviewService.setEnrollment(enrollment.enrollmentId, enable, loggedInUser.id)
				: await partnerPreviewService.createEnrollment(machine.id, enable, loggedInUser.id);

			await partnerPreviewService.pollJob(jobId);
			await loadPartnerPreviewEnrollments();

			pushSuccessfulNotification(
				enable ? 'Partner Preview enabled for this machine.' : 'Partner Preview disabled for this machine.'
			);
		} catch (error) {
			pushDangerNotification(
				typeof error === 'string' ? error : error?.response?.data?.detail || 'Failed to update Partner Preview enrollment.'
			);

			throw error;
		}
	}, [enrollmentsByMachineId, loggedInUser.id, partnerPreviewService, loadPartnerPreviewEnrollments, pushSuccessfulNotification, pushDangerNotification]);

	if (loading) {
		return <Loading />;
	}

	if (loadFailed) {
		return (
			<div className="min-vh-80 shadow p-3 bg-white rounded">
				<LoadFailedMessage subject="The inventory" onRetry={retryLoadingMachines} />
			</div>
		);
	}

	return (
		<div className="min-vh-80 shadow p-3 bg-white rounded">
			{machines.length ? (
				<>
					<div className="min-vh-80 row">
						<div className="col-9 border-right inventory-table">
							<PartnerPreviewProgramBanner
								participating={partnerPreviewEnabled}
								participationLoaded={partnerPreviewParticipationLoaded}
								onEnrolled={loadPartnerPreviewParticipation}
							/>
							{!bannerDismissalsLoading && hasLegacyOSVersionMachine && !legacyOSVersionBannerDismissed && (
								<LegacyOSVersionBanner onDismiss={handleDismissLegacyOSVersionBanner} />
							)}
							<Table
								data={tableMachines}
								keyField="serialNumber"
								search
								defaultSortDirection="asc"
								selectRow={{
									mode: 'radio',
									classes: 'selected',
									clickToSelect: true,
									hideSelectColumn: true,
									selected: [selectedMachine?.serialNumber],
									onSelect: (device) => { return setSelectedMachine(device); },
								}}
							>
								{[
									<Table.Col key="model" renderCell={renderModel} align="left">
										Model
									</Table.Col>,
									<Table.Col key="serialNumber" field="serialNumber" align="left">
										Serial Number
									</Table.Col>,
									<Table.Col key="name" renderCell={renderHostName} field="name" align="left">
										Host Name
									</Table.Col>,
									<Table.Col
										key="location"
										renderCell={renderLocation}
										field="location"
										align="left"
										filter="select"
										filterOptions={locationFilter()}
										filterDefault={defaultFilterLocation}
									>
										Location
									</Table.Col>,
									<Table.Col key="osVersion" renderCell={renderOSVersion} field="osVersion" align="left">
										{/* Table.Col's header only accepts a single valid element - "OS {icon}" as sibling
										children is an array, so it must be wrapped in one <span>. */}
										<span className="d-flex align-items-center">
											OS
											{!bannerDismissalsLoading && hasLegacyOSVersionMachine && legacyOSVersionBannerDismissed && (
												<InfoIcon
													placement="right"
													text="Some machines report an OS version below 5.4.4, which might not be reported correctly."
													iconType="exclamation"
													iconColor="#f4be00"
													tooltipStyle="ms-2"
												/>
											)}
										</span>
									</Table.Col>,
									partnerPreviewEnabled && (
										<Table.Col key="upgradeChannel" renderCell={renderUpgradeChannel} align="left">
											Version Type
										</Table.Col>
									),
									<Table.Col
										key="expirationDate"
										renderCell={renderExpirationDate}
										field="expirationDate"
										sort={compareExpirationDates}
										searchable={false}
										align="left"
									>
										Expiration Date
									</Table.Col>,
									<Table.Col
										key="addons"
										renderCell={renderAddonsAlert}
										field="addonsAlert"
										sort={compareAddonsAlerts}
										searchable={false}
										align="left"
									>
										Add-ons
									</Table.Col>,
									partnerPreviewEnabled && (
										<Table.Col key="partnerPreview" width={40} align="center" renderCell={renderPartnerPreviewInfo} />
									),
								].filter(Boolean)}
							</Table>
						</div>

						<div className="col-3 p-4 inventory-details-pane">
							<Does
								loggedInUser
								havePermission="appliance.view"
								onAnyResource={true}
								yes={() => {
									return selectedMachine ? (
										<MachineDetails
											machine={selectedMachine}
											generateMaintenanceCertificationPdf={generateMaintenanceCertificationPdf}
											partnerPreviewParticipating={partnerPreviewEnabled}
											partnerPreviewEnrollmentEnabled={Boolean(enrollmentsByMachineId[selectedMachine.id]?.enabled)}
											partnerPreviewEnrollmentsRefreshCount={enrollmentsRefreshCount}
											currentUpgradeChannel={productsByMachineId[selectedMachine.id]?.currentUpgradeChannel ?? null}
											addons={buildAddonsPanel(productsByMachineId[selectedMachine.id], {
												interval: expiringInterval,
												loadFailed: productsLoadFailed,
											})}
											expiringInterval={expiringInterval}
											addonsSectionRequest={addonsSectionRequest}
											onAddonsSectionRequestHandled={handleAddonsSectionRequestHandled}
											canManagePartnerPreview={accessService.hasRoleOnCompanyResource('fleet.admin')}
											onTogglePreviewUpdates={togglePartnerPreviewForMachine}
										/>
									) : null;
								}
								}
							/>
						</div>
					</div>
				</>
			) : (
				<p>No machines</p>
			)}
		</div>
	);
};
